import dayjs from 'dayjs'
import Button from '@Components/Buttons/Button'
import React, { JSX } from 'react'
import {
    formatOverclaimDocumentStatus,
    OverclaimUploadedDocument,
} from '@Entities/overclaim/overclaim-uploaded-document'
import { ICONS } from '@Const/common'
import clsx from 'clsx'
import { startCase } from 'lodash'

interface DocumentUploadTableRowCellsProps {
    uploadedDocument: OverclaimUploadedDocument
    onAddDescriptionToDocumentClicked: (uploadedDocument: OverclaimUploadedDocument) => void
    onDownloadDocumentClicked: (overclaimMemberUploadId: number) => void
    canEditDescription: boolean
    isExpanded: boolean
    onExpandedClick: () => void
}

// TODO - Returning an array of elements from a component is supported by react, but is blocked by TS issues https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/57430
export const generateDocumentUploadTableCells = ({
    uploadedDocument,
    onAddDescriptionToDocumentClicked,
    onDownloadDocumentClicked,
    canEditDescription,
    isExpanded,
    onExpandedClick,
}: DocumentUploadTableRowCellsProps): JSX.Element[] => {
    const bytesToKB = (bytes: number) => (bytes / 1024).toFixed(2) + 'KB'
    const hasRejectionReason = !!uploadedDocument.documentRejectionReason
    const formatStatus = (status: string) => startCase(status.toLowerCase())

    return [
        <div
            key={`${uploadedDocument.name}-overclaim-document-upload-table-info`}
            className="overclaim-document-upload-table-info"
        >
            <div className="overclaim-upload-document-name">{uploadedDocument.name}</div>
            <div>{dayjs(uploadedDocument.createdAt).format('MMM DD, YYYY')}</div>
            {uploadedDocument.description && (
                <div className="overclaim-description-text">{uploadedDocument.description}</div>
            )}
            {canEditDescription && (
                <Button
                    className="overclaim-description-button"
                    mainColor
                    type="secondary-small"
                    onClick={() => onAddDescriptionToDocumentClicked(uploadedDocument)}
                >
                    {uploadedDocument.description ? 'Edit Description' : 'Add Description'}
                </Button>
            )}
        </div>,
        <div key={`${uploadedDocument.name}-overclaim-download-section`} className="overclaim-download-section">
            {hasRejectionReason && (
                <img
                    src={isExpanded ? ICONS.chevron_drop_up : ICONS.chevron_drop_down}
                    onClick={onExpandedClick}
                    alt="expand"
                />
            )}
            <div>{formatOverclaimDocumentStatus(uploadedDocument.status)}</div>
            <div className="overclaim-download-container">
                <Button
                    className="overclaim-download-button"
                    mainColor
                    type="secondary-small"
                    onClick={() => onDownloadDocumentClicked(uploadedDocument.memberUploadId)}
                >
                    Download
                </Button>
                <div className="overclaim-document-size">{bytesToKB(uploadedDocument.size)}</div>
            </div>
        </div>,
        <div
            key={`${uploadedDocument.name}-overclaim-rejection-section`}
            className={clsx('overclaim-rejection-section', isExpanded && 'expanded')}
        >
            {uploadedDocument.documentRejectionReason && (
                <>
                    <div className="overclaim-rejection-reason">
                        {formatStatus(uploadedDocument.documentRejectionReason)}
                    </div>
                    <div className="overclaim-rejection-description">
                        {uploadedDocument.rejectionDescription || 'No description provided'}
                    </div>
                </>
            )}
        </div>,
    ]
}
